import ThemedSelect from '../../common/ThemedSelect';
import React, { useState, useEffect } from 'react';
import { X, ToggleLeft, ToggleRight } from 'lucide-react';
import { toast } from 'react-toastify';
import DatePicker from 'react-datepicker';
import { parseISO } from 'date-fns';
import { toApiDateString } from '../../../utils/dateUtils';
import api from '../../../services/weighbridgeComplianceApi';

const MAINTENANCE_TYPES = [
  'Daily Check',
  'Weekly Check',
  'Monthly Inspection',
  'Annual Service',
  'Repair',
  'Ad-hoc',
];
const PRIORITY_OPTIONS = ['Low', 'Medium', 'High', 'Critical'];
const STATUS_OPTIONS = ['Scheduled', 'Completed', 'Overdue', 'Cancelled'];

const initialForm = {
  assetId: '',
  maintenanceType: '',
  priority: 'Medium',
  scheduledDate: null,
  completedDate: null,
  assignedTo: '',
  workOrderNumber: '',
  performedBy: '',
  isExternalContractor: false,
  findings: '',
  actionsTaken: '',
  followUpRequired: false,
  followUpNotes: '',
  reVerificationRequired: false,
  costEstimate: '',
  actualCost: '',
  status: 'Scheduled',
};

export const MaintenanceModal = ({ record, assets = [], onClose, onSave }) => {
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const isEdit = Boolean(record);

  useEffect(() => {
    if (record) {
      setForm({
        assetId: record.asset_id || '',
        maintenanceType: record.maintenance_type || '',
        priority: record.priority || 'Medium',
        scheduledDate: record.scheduled_date ? parseISO(record.scheduled_date) : null,
        completedDate: record.completed_date ? parseISO(record.completed_date) : null,
        assignedTo: record.assigned_to || '',
        workOrderNumber: record.work_order_number || '',
        performedBy: record.performed_by || '',
        isExternalContractor: record.is_external_contractor || false,
        findings: record.findings || '',
        actionsTaken: record.actions_taken || '',
        followUpRequired: record.follow_up_required || false,
        followUpNotes: record.follow_up_notes || '',
        reVerificationRequired: record.re_verification_required || false,
        costEstimate: record.cost_estimate != null ? String(record.cost_estimate) : '',
        actualCost: record.actual_cost != null ? String(record.actual_cost) : '',
        status: record.status || 'Scheduled',
      });
    } else {
      setForm(initialForm);
    }
    setErrors({});
  }, [record]);

  const validate = () => {
    const newErrors = {};
    if (!form.assetId) newErrors.assetId = 'Asset is required';
    if (!form.maintenanceType) newErrors.maintenanceType = 'Maintenance type is required';
    if (!form.priority) newErrors.priority = 'Priority is required';
    if (!form.scheduledDate) newErrors.scheduledDate = 'Scheduled date is required';
    if (!form.status) newErrors.status = 'Status is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const payload = {
        asset_id: form.assetId ? Number(form.assetId) : undefined,
        maintenance_type: form.maintenanceType,
        priority: form.priority,
        scheduled_date: form.scheduledDate ? toApiDateString(form.scheduledDate) : null,
        completed_date: form.completedDate ? toApiDateString(form.completedDate) : null,
        assigned_to: form.assignedTo,
        work_order_number: form.workOrderNumber,
        performed_by: form.performedBy,
        is_external_contractor: form.isExternalContractor,
        findings: form.findings,
        actions_taken: form.actionsTaken,
        follow_up_required: form.followUpRequired,
        follow_up_notes: form.followUpNotes,
        re_verification_required: form.reVerificationRequired,
        cost_estimate: form.costEstimate ? parseFloat(form.costEstimate) : null,
        actual_cost: form.actualCost ? parseFloat(form.actualCost) : null,
        status: form.status,
      };
      if (isEdit) {
        await api.maintenance.update(record.id, payload);
        toast.success('Maintenance record updated');
      } else {
        await api.maintenance.create(payload);
        toast.success('Maintenance record created');
      }
      onSave();
    } catch (error) {
      const msg = error.response?.data?.message || 'Failed to save maintenance record';
      toast.error(msg);
      console.error('Error saving maintenance:', error);
    } finally {
      setSaving(false);
    }
  };

  const Toggle = ({ value, onChange, label }) => (
    <div
      className="flex items-center gap-2 cursor-pointer select-none"
      onClick={() => onChange(!value)}
    >
      {value ? (
        <ToggleRight className="w-8 h-5 text-teal-500" />
      ) : (
        <ToggleLeft className="w-8 h-5 text-slate-300" />
      )}
      <span className="text-sm text-slate-700">{label}</span>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-surface rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-slate-200">
          <h2
            className="text-lg font-bold text-slate-800"
            style={{ fontFamily: 'DM Sans, sans-serif' }}
          >
            {isEdit ? 'Edit Maintenance Record' : 'Schedule Maintenance'}
          </h2>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
                Asset *
              </label>
              <ThemedSelect
                value={form.assetId}
                onChange={(e) => setForm({ ...form, assetId: e.target.value })}
                className={`w-full px-3 py-2.5 border ${errors.assetId ? 'border-red-300' : 'border-slate-200'} rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm`}
              >
                <option value="">Select asset...</option>
                {assets.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name || a.asset_name}
                  </option>
                ))}
              </ThemedSelect>
              {errors.assetId && <p className="text-xs text-red-500 mt-1">{errors.assetId}</p>}
            </div>

            <div>
              <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
                Maintenance Type *
              </label>
              <ThemedSelect
                value={form.maintenanceType}
                onChange={(e) => setForm({ ...form, maintenanceType: e.target.value })}
                className={`w-full px-3 py-2.5 border ${errors.maintenanceType ? 'border-red-300' : 'border-slate-200'} rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm`}
              >
                <option value="">Select type...</option>
                {MAINTENANCE_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </ThemedSelect>
              {errors.maintenanceType && (
                <p className="text-xs text-red-500 mt-1">{errors.maintenanceType}</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
                Priority *
              </label>
              <ThemedSelect
                value={form.priority}
                onChange={(e) => setForm({ ...form, priority: e.target.value })}
                className={`w-full px-3 py-2.5 border ${errors.priority ? 'border-red-300' : 'border-slate-200'} rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm`}
              >
                <option value="">Select priority...</option>
                {PRIORITY_OPTIONS.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </ThemedSelect>
              {errors.priority && <p className="text-xs text-red-500 mt-1">{errors.priority}</p>}
            </div>

            <div>
              <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
                Status *
              </label>
              <ThemedSelect
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
                className={`w-full px-3 py-2.5 border ${errors.status ? 'border-red-300' : 'border-slate-200'} rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm`}
              >
                <option value="">Select status...</option>
                {STATUS_OPTIONS.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </ThemedSelect>
              {errors.status && <p className="text-xs text-red-500 mt-1">{errors.status}</p>}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
                Scheduled Date *
              </label>
              <DatePicker
                selected={form.scheduledDate}
                onChange={(date) => setForm({ ...form, scheduledDate: date })}
                dateFormat="yyyy-MM-dd"
                placeholderText="Select date"
                className={`w-full px-3 py-2.5 border ${errors.scheduledDate ? 'border-red-300' : 'border-slate-200'} rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm`}
              />
              {errors.scheduledDate && (
                <p className="text-xs text-red-500 mt-1">{errors.scheduledDate}</p>
              )}
            </div>

            <div>
              <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
                Completed Date
              </label>
              <DatePicker
                selected={form.completedDate}
                onChange={(date) => setForm({ ...form, completedDate: date })}
                dateFormat="yyyy-MM-dd"
                placeholderText="Select date"
                className="w-full px-3 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
                Assigned To
              </label>
              <input
                type="text"
                value={form.assignedTo}
                onChange={(e) => setForm({ ...form, assignedTo: e.target.value })}
                className="w-full px-3 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm"
                placeholder="Assigned personnel"
              />
            </div>

            <div>
              <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
                Work Order Number
              </label>
              <input
                type="text"
                value={form.workOrderNumber}
                onChange={(e) => setForm({ ...form, workOrderNumber: e.target.value })}
                className="w-full px-3 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm"
                placeholder="e.g. WO-2026-001"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
                Performed By
              </label>
              <input
                type="text"
                value={form.performedBy}
                onChange={(e) => setForm({ ...form, performedBy: e.target.value })}
                className="w-full px-3 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm"
                placeholder="Technician name"
              />
            </div>

            <div className="flex items-end pb-1">
              <Toggle
                value={form.isExternalContractor}
                onChange={(val) => setForm({ ...form, isExternalContractor: val })}
                label="External Contractor"
              />
            </div>
          </div>

          <div>
            <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
              Findings
            </label>
            <textarea
              value={form.findings}
              onChange={(e) => setForm({ ...form, findings: e.target.value })}
              rows={3}
              className="w-full px-3 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm resize-none"
              placeholder="Describe findings during maintenance..."
            />
          </div>

          <div>
            <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
              Actions Taken
            </label>
            <textarea
              value={form.actionsTaken}
              onChange={(e) => setForm({ ...form, actionsTaken: e.target.value })}
              rows={3}
              className="w-full px-3 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm resize-none"
              placeholder="Describe actions taken..."
            />
          </div>

          <div className="space-y-3 pt-2">
            <Toggle
              value={form.followUpRequired}
              onChange={(val) => setForm({ ...form, followUpRequired: val })}
              label="Follow-up Required"
            />
            {form.followUpRequired && (
              <div>
                <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
                  Follow-up Notes
                </label>
                <textarea
                  value={form.followUpNotes}
                  onChange={(e) => setForm({ ...form, followUpNotes: e.target.value })}
                  rows={2}
                  className="w-full px-3 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm resize-none"
                  placeholder="Describe required follow-up..."
                />
              </div>
            )}
          </div>

          {form.maintenanceType === 'Repair' && (
            <div className="pt-1">
              <Toggle
                value={form.reVerificationRequired}
                onChange={(val) => setForm({ ...form, reVerificationRequired: val })}
                label="Re-verification Required"
              />
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
            <div>
              <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
                Cost Estimate (R)
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={form.costEstimate}
                onChange={(e) => setForm({ ...form, costEstimate: e.target.value })}
                className="w-full px-3 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm"
                placeholder="0.00"
              />
            </div>

            <div>
              <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
                Actual Cost (R)
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={form.actualCost}
                onChange={(e) => setForm({ ...form, actualCost: e.target.value })}
                className="w-full px-3 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm"
                placeholder="0.00"
              />
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 p-6 border-t border-slate-200">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-slate-200 rounded-lg hover:bg-slate-50 text-sm text-slate-600"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? 'Saving...' : isEdit ? 'Update Record' : 'Schedule Maintenance'}
          </button>
        </div>
      </div>
    </div>
  );
};
